import { useEffect, useState, type ReactNode } from 'react';
import type { ListaMiembros } from '../../api/operaciones';
import type { Miembro } from '../../api/ops/equipo';
import { conDosPasosEnElMomento } from '../../api/dosPasosEnElMomento';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { EstadoError } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { Interruptor } from '../../componentes/Interruptor';
import { Paginacion } from '../../componentes/Paginacion';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Tabla, type FilaTabla } from '../../componentes/Tabla';
import { Ventana } from '../../componentes/Ventana';
import { iniciales } from '../../logica/navegacion';
import { estadoDeError } from '../../logica/estados';
import { haySiguiente, usePaginacionCursor } from '../../logica/paginacion';
import { haceCuanto } from '../../logica/tiempo';
import { usePedirCodigoDosPasos } from '../../marco/dosPasos';
import { Avatar } from '../../marco/piezas';
import { PERMISOS, type ContextoEquipo } from './contexto';
import { MOTIVO_MINIMO, enElFuturo, fechaLarga, mesYAnio, nombreDePila, rolesDeMiembro, textoDeError } from './textos';

/**
 * Miembros (1.9): tabla en ordenador (persona, roles, dos pasos, último acceso, PIN e invitado por; G179, G336) y tarjetas en móvil, con
 * «Anterior / Siguiente» (G121). La ficha se abre en RV Panel con sus apartados Datos · Roles · PIN de tablet; desde ella
 * se cambian los roles y las sedes (`updateMember` con `If-Match`), se pide un PIN nuevo (`deleteMemberPin`, G174), se
 * retira del equipo con un motivo opcional (G336; G219: si iba a recibir la propiedad, la transferencia se cancela) y se transfiere la propiedad
 * (`createOwnershipTransfer` con dos pasos en el momento, G116; caduca a los 7 días, G218).
 */
type ApartadoFicha = 'datos' | 'roles' | 'pin';
type Ventanas = 'transferir' | 'retirar' | 'pin' | null;

const PESTANAS: [ApartadoFicha, string, NombreIcono][] = [
    ['datos', 'Datos', 'user'],
    ['roles', 'Roles', 'user-cog'],
    ['pin', 'PIN de tablet', 'key-round'],
];

export function Miembros({ c, cifra }: { c: ContextoEquipo; cifra: (n: number) => void }) {
    const pag = usePaginacionCursor();
    const [lista, setLista] = useState<ListaMiembros | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [abierto, setAbierto] = useState<Miembro | null>(null);
    const [ahora] = useState(() => Date.now());
    const { ops, negocio } = c;

    useEffect(() => {
        let vivo = true;
        ops.listMembers(negocio, { cursor: pag.cursor })
            .then((l) => {
                if (vivo) {
                    setLista(l);
                    setError(null);
                    cifra(l.total_count);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, pag.cursor, intento, cifra]);

    const actualizar = () => {
        setIntento((i) => i + 1);
        c.refrescar();
    };

    if (error !== null && lista === null) {
        return <EstadoError seccion="Miembros" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={c.movil} />;
    }
    if (lista === null) {
        return null;
    }

    const filas: FilaTabla[] = lista.data.map((m) => {
        const tu = m.id === c.yo;
        const persona = (
            <span className="rv-eq-persona">
                <Avatar texto={iniciales(m.display_name)} tamano={36} fondo={m.is_owner ? 'var(--rv-red)' : 'var(--rv-ink)'} tinta="#fff" />
                <span>
                    <strong>
                        {m.display_name}
                        {tu && <em className="rv-eq-tu">Tú</em>}
                    </strong>
                    <span>{m.email}</span>
                </span>
            </span>
        );
        return {
            clave: m.id,
            etiqueta: `Abrir la ficha de ${m.display_name}`,
            onAbrir: () => {
                setAbierto(m);
            },
            celdas: {
                persona,
                roles: rolesDeMiembro(m),
                dos: <DosPasos activa={m.two_factor_enabled === true} />,
                acceso: m.last_active_at == null ? '—' : primeraMayuscula(haceCuanto(m.last_active_at)),
                pin: <Pin m={m} ahora={ahora} />,
                por: invitadoPor(m),
            },
            tarjeta: (
                <>
                    {persona}
                    <span className="rv-eq-linea">{rolesDeMiembro(m)}</span>
                </>
            ),
        };
    });

    return (
        <>
            <Tabla
                etiqueta="Miembros del equipo"
                movil={c.movil}
                columnas={[
                    { clave: 'persona', titulo: 'Persona', ancho: 'minmax(0, 2.2fr)' },
                    { clave: 'roles', titulo: 'Roles', ancho: 'minmax(0, 1.6fr)' },
                    { clave: 'dos', titulo: 'Dos pasos', ancho: 'minmax(0, 1fr)' },
                    { clave: 'acceso', titulo: 'Último acceso', ancho: 'minmax(0, 1fr)' },
                    { clave: 'pin', titulo: 'PIN', ancho: 'minmax(0, 1fr)' },
                    { clave: 'por', titulo: 'Invitado por', ancho: 'minmax(0, 1.2fr)' },
                ]}
                filas={filas}
            />
            {(pag.hayAnterior || haySiguiente(lista.next_cursor)) && (
                <div className="rv-eq-pie">
                    <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={c.movil} />
                </div>
            )}
            <FichaMiembro
                key={abierto?.id ?? 'ninguno'}
                c={c}
                miembro={abierto}
                onCerrar={() => {
                    setAbierto(null);
                }}
                onCambio={(m) => {
                    setAbierto(m);
                    actualizar();
                }}
            />
        </>
    );
}

/** «Invitado por» (G336): nulo para quien creó el negocio o entró sin invitación. */
function invitadoPor(m: Miembro): string {
    return m.invited_by?.name ?? '—';
}

function primeraMayuscula(t: string): string {
    return t.charAt(0).toUpperCase() + t.slice(1);
}

function DosPasos({ activa }: { activa: boolean }) {
    return (
        <span className={`rv-eq-estado${activa ? '' : ' rv-eq-estado--apagado'}`}>
            <Icono nombre={activa ? 'shield-check' : 'shield'} />
            {activa ? 'Activa' : 'Sin activar'}
        </span>
    );
}

function Pin({ m, ahora }: { m: Miembro; ahora: number }) {
    if (enElFuturo(m.pin_locked_until, ahora)) {
        return <Chip estado="problem">Bloqueado</Chip>;
    }
    return (
        <span className={`rv-eq-estado${m.has_pin === true ? '' : ' rv-eq-estado--apagado'}`}>
            <Icono nombre={m.has_pin === true ? 'check' : 'minus'} />
            {m.has_pin === true ? 'Elegido' : 'Sin elegir'}
        </span>
    );
}

function Linea({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
    return (
        <div className="rv-eq-dato">
            <span>{etiqueta}</span>
            <strong>{children}</strong>
        </div>
    );
}

/** La hora (HH:MM) de un instante en la zona horaria del negocio (G362); sin ella, la del dispositivo. */
function horaEnZona(iso: string, zona: string | null): string {
    return new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit', ...(zona === null ? {} : { timeZone: zona }) }).format(new Date(iso));
}

function FichaMiembro({ c, miembro, onCerrar, onCambio }: { c: ContextoEquipo; miembro: Miembro | null; onCerrar: () => void; onCambio: (m: Miembro | null) => void }) {
    const [apartado, setApartado] = useState<ApartadoFicha>('datos');
    const [roles, setRoles] = useState<string[]>(miembro?.roles.map((r) => r.id) ?? []);
    const [sedes, setSedes] = useState<string[]>(miembro?.location_ids ?? []);
    const [ventana, setVentana] = useState<Ventanas>(null);
    const [ocupado, setOcupado] = useState(false);
    const [confirmar, setConfirmar] = useState('');
    const [motivo, setMotivo] = useState('');
    const [pinPedido, setPinPedido] = useState(false);
    const [ahora] = useState(() => Date.now());
    const pedirCodigo = usePedirCodigoDosPasos();
    const { ops, negocio, permisos, avisar } = c;
    const gestionar = permisos.puede(PERMISOS.gestionar) && !c.soloLectura;
    const m = miembro;
    // «Tablets donde puede entrar» (`Member.tablets`, G362): solo vienen en la ficha (getMember), no en la lista.
    const [deFicha, setDeFicha] = useState<{ id: string; tablets: NonNullable<Miembro['tablets']> } | null>(null);
    const idMiembro = miembro?.id ?? null;
    const tablets = deFicha !== null && deFicha.id === idMiembro ? deFicha.tablets : null;
    useEffect(() => {
        if (idMiembro === null) {
            return;
        }
        let vivo = true;
        ops.getMember(negocio, idMiembro)
            .then(({ datos }) => {
                if (vivo) setDeFicha({ id: idMiembro, tablets: datos.tablets ?? [] });
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, idMiembro]);


    if (m === null) {
        return (
            <PanelLateral abierto={false} onCerrar={onCerrar} titulo="" movil={c.movil}>
                {null}
            </PanelLateral>
        );
    }

    const tu = m.id === c.yo;
    const nombre = nombreDePila(m.display_name);
    const haciaEl = c.transferencia !== null && c.transferencia.to_member_id === m.id ? c.transferencia : null;
    const puedeTransferir = c.soyPropietario && permisos.puede(PERMISOS.transferir) && !m.is_owner && !c.soloLectura;
    const cambiados = apartado === 'roles' && (roles.join() !== m.roles.map((r) => r.id).join() || sedes.join() !== m.location_ids.join());
    const nombreOk = confirmar.trim().toLocaleLowerCase('es') === c.nombreNegocio.trim().toLocaleLowerCase('es') && confirmar.trim() !== '';

    const guardar = () => {
        setOcupado(true);
        ops.getMember(negocio, m.id)
            .then(({ version }) => ops.updateMember(negocio, m.id, { role_ids: roles, location_ids: sedes }, version ?? ''))
            .then(({ datos }) => {
                avisar({ tipo: 'ok', texto: 'Cambios guardados.' });
                onCambio(datos);
                if (tu) {
                    c.recargarMarco();
                }
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido guardar los cambios. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const pedirPin = () => {
        setOcupado(true);
        ops.deleteMemberPin(negocio, m.id)
            .then(() => {
                setVentana(null);
                setPinPedido(true);
                onCambio({ ...m, has_pin: false, pin_locked_until: null });
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido borrar su PIN. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    // Motivo opcional (G336); si se escribe, como el contrato: de 10 caracteres o más.
    const motivoEscrito = motivo.trim();
    const motivoCorto = motivoEscrito !== '' && motivoEscrito.length < MOTIVO_MINIMO;
    const retirar = () => {
        if (motivoCorto) {
            return;
        }
        setOcupado(true);
        ops.removeMember(negocio, m.id, motivoEscrito === '' ? null : motivoEscrito)
            .then(() => {
                setVentana(null);
                avisar({ tipo: 'ok', texto: `${m.display_name} ya no está en el equipo.` });
                onCambio(null);
                onCerrar();
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido retirarle del equipo. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const transferir = () => {
        if (!nombreOk) {
            return;
        }
        setOcupado(true);
        conDosPasosEnElMomento(ops, pedirCodigo, (clave) => ops.createOwnershipTransfer(negocio, m.id, clave))
            .then(() => {
                setVentana(null);
                c.refrescar();
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido proponer la transferencia. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const cancelarTransferencia = () => {
        if (haciaEl === null) {
            return;
        }
        ops.cancelOwnershipTransfer(negocio, haciaEl.id)
            .then(() => {
                c.refrescar();
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido cancelar la transferencia. Prueba de nuevo.') });
            });
    };

    const notaPin = tu
        ? 'Tu PIN lo cambias en Mi cuenta. De 4 a 6 cifras; 5 fallos seguidos lo bloquean 15 minutos.'
        : m.has_pin === true
          ? 'De 4 a 6 cifras y solo lo sabe esta persona. 5 fallos seguidos lo bloquean 15 minutos. Si lo ha olvidado, pídele uno nuevo y lo elegirá en Mi cuenta.'
          : 'Lo elige la propia persona en Mi cuenta, de 4 a 6 cifras. Hasta entonces no puede entrar en las tablets.';
    const bloqueado = enElFuturo(m.pin_locked_until, ahora);

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={onCerrar}
                movil={c.movil}
                tamano="ficha"
                eyebrow="Miembro"
                titulo={m.display_name}
                subtitulo={`${rolesDeMiembro(m)} • ${m.email}`}
                accion={cambiados && gestionar ? 'Guardar' : ''}
                ocupado={ocupado && ventana === null}
                onAccion={guardar}
            >
                <div className="rv-eq-botonera" role="tablist" aria-label="Apartados de la ficha">
                    {PESTANAS.map(([clave, texto, icono]) => (
                        <button
                            key={clave}
                            type="button"
                            role="tab"
                            aria-selected={apartado === clave}
                            className="rv-eq-botonera__b"
                            onClick={() => {
                                setApartado(clave);
                            }}
                        >
                            <Icono nombre={icono} />
                            <span>{texto}</span>
                        </button>
                    ))}
                </div>

                {apartado === 'datos' && (
                    <div className="rv-eq-ficha">
                        <h3 className="rv-eq-ceja">Datos</h3>
                        <Linea etiqueta="Email">{m.email}</Linea>
                        <Linea etiqueta="Roles">{rolesDeMiembro(m)}</Linea>
                        <Linea etiqueta="Dos pasos">
                            <DosPasos activa={m.two_factor_enabled === true} />
                        </Linea>
                        <Linea etiqueta="Último acceso">{m.last_active_at == null ? '—' : primeraMayuscula(haceCuanto(m.last_active_at))}</Linea>
                        <Linea etiqueta="Invitado por">{invitadoPor(m)}</Linea>
                        <Linea etiqueta="En el equipo desde">{mesYAnio(m.joined_at)}</Linea>
                        <p className="rv-eq-nota">Nombre, email y contraseña los cambia cada persona desde Mi cuenta.</p>

                        {haciaEl !== null && (
                            <div className="rv-eq-caja">
                                <span className="rv-eq-caja__titulo">
                                    <Icono nombre="crown" />
                                    Transferir la propiedad
                                    <Chip estado="pending">Pendiente de aceptar</Chip>
                                </span>
                                <span>{`${nombre} tiene hasta ${fechaLarga(haciaEl.expires_at)} para aceptarla desde el aviso o el email. Hasta entonces sigues siendo el propietario.`}</span>
                                {puedeTransferir && (
                                    <Boton tipo="secundario" icono="x" onClick={cancelarTransferencia}>
                                        Cancelar
                                    </Boton>
                                )}
                            </div>
                        )}
                        {haciaEl === null && c.transferencia === null && puedeTransferir && (
                            <div className="rv-eq-caja">
                                <span className="rv-eq-caja__titulo">
                                    <Icono nombre="crown" />
                                    Transferir la propiedad
                                </span>
                                {/* G217: quien deja la propiedad queda como Administrador, además de sus otros roles. */}
                                <span>{`${m.display_name} pasará a ser el propietario del negocio, con la facturación y el plan. Le llega un aviso en la campana y un email para aceptarla o rechazarla. Tú quedas como Administrador, además de tus otros roles.`}</span>
                                <Boton
                                    tipo="secundario"
                                    icono="crown"
                                    onClick={() => {
                                        setConfirmar('');
                                        setVentana('transferir');
                                    }}
                                >
                                    {`Transferir a ${nombre}`}
                                </Boton>
                            </div>
                        )}
                        {gestionar && !m.is_owner && !tu && (
                            <Boton
                                tipo="secundario"
                                rojo
                                icono="user-minus"
                                onClick={() => {
                                    setMotivo('');
                                    setVentana('retirar');
                                }}
                            >
                                Retirar del equipo
                            </Boton>
                        )}
                    </div>
                )}

                {apartado === 'roles' && (
                    <div className="rv-eq-ficha">
                        <h3 className="rv-eq-ceja">Roles</h3>
                        {m.is_owner && <Interruptor etiqueta="Propietario" ayuda="No se marca aquí: se transfiere desde Datos." valor bloqueado />}
                        {c.roles.map((r) => (
                            <Interruptor
                                key={r.id}
                                etiqueta={r.name}
                                ayuda={r.description ?? (r.kind === 'custom' ? 'Rol propio de tu negocio.' : undefined)}
                                valor={roles.includes(r.id)}
                                desactivado={!gestionar}
                                onChange={(on) => {
                                    setRoles((actual) => (on ? [...actual, r.id] : actual.filter((x) => x !== r.id)));
                                }}
                            />
                        ))}
                        <p className="rv-eq-nota">Una persona puede tener varios roles. Suma los permisos de todos.</p>
                        {c.sedes.length > 1 && (
                            <>
                                <h3 className="rv-eq-ceja">Sedes</h3>
                                {c.sedes.map((s) => (
                                    <Interruptor
                                        key={s.id}
                                        etiqueta={s.name}
                                        valor={sedes.includes(s.id)}
                                        desactivado={!gestionar}
                                        onChange={(on) => {
                                            setSedes((actual) => (on ? [...actual, s.id] : actual.filter((x) => x !== s.id)));
                                        }}
                                    />
                                ))}
                                <p className="rv-eq-nota">Sin ninguna marcada, trabaja en todas las sedes.</p>
                            </>
                        )}
                    </div>
                )}

                {apartado === 'pin' && (
                    <div className="rv-eq-ficha">
                        <h3 className="rv-eq-ceja">PIN de tablet</h3>
                        <Linea etiqueta="PIN">
                            {bloqueado && m.pin_locked_until != null ? `Bloqueado hasta las ${horaEnZona(m.pin_locked_until, c.zonaHoraria)}` : m.has_pin === true ? 'Elegido' : 'Sin elegir'}
                        </Linea>
                        <p className="rv-eq-nota">{notaPin}</p>
                        {tablets !== null && <Linea etiqueta="Tablets donde puede entrar">{tablets.length === 0 ? 'Ninguna' : tablets.map((t) => t.name).join(', ')}</Linea>}
                        {pinPedido ? (
                            <div role="status" className="rv-eq-hecho">
                                <Icono nombre="check" />
                                Le hemos avisado para que elija un PIN nuevo. Hasta entonces no entra en las tablets.
                            </div>
                        ) : (
                            gestionar &&
                            !tu &&
                            m.has_pin === true && (
                                <Boton
                                    tipo="secundario"
                                    rojo
                                    icono="trash-2"
                                    onClick={() => {
                                        setVentana('pin');
                                    }}
                                >
                                    Pedir un PIN nuevo
                                </Boton>
                            )
                        )}
                    </div>
                )}
            </PanelLateral>

            <Ventana
                abierta={ventana === 'transferir'}
                titulo={`Transferir la propiedad a ${m.display_name}`}
                texto={`Le llega un email y tiene que aceptarla. Hasta entonces sigues siendo el propietario y puedes cancelarla. Para confirmar, escribe «${c.nombreNegocio}».`}
                accion="Transferir"
                accionIcono="crown"
                inactiva={!nombreOk}
                ocupado={ocupado}
                ocupadoTexto="Transfiriendo…"
                movil={c.movil}
                onAccion={transferir}
                onCerrar={() => {
                    setVentana(null);
                }}
            >
                <Campo etiqueta="Nombre del negocio" valor={confirmar} onChange={setConfirmar} autocompletar="off" mayusculas="none" nombre="confirmar-negocio" onEnter={transferir} />
            </Ventana>
            <Ventana
                abierta={ventana === 'retirar'}
                titulo={`¿Retirar a ${m.display_name} del equipo?`}
                // G219: si iba a recibir la propiedad, la transferencia pendiente se cancela.
                texto={`Pierde el acceso al panel y a las tablets al momento. Lo que hizo sigue en Actividad.${haciaEl !== null ? ' Se cancela la transferencia pendiente.' : ''}`}
                accion="Retirar del equipo"
                accionIcono="user-minus"
                inactiva={motivoCorto}
                ocupado={ocupado}
                ocupadoTexto="Retirando…"
                movil={c.movil}
                onAccion={retirar}
                onCerrar={() => {
                    setVentana(null);
                }}
            >
                {/* Textos propuestos: el campo del motivo aún no está dibujado (encargo de la exportación del 2026-10-22, punto 22). */}
                <Campo etiqueta="Motivo (opcional)" valor={motivo} onChange={setMotivo} filas={2} max={500} ayuda="Si lo escribes, queda en Actividad. Al menos 10 caracteres." nombre="motivo-retirar" />
            </Ventana>
            <Ventana
                abierta={ventana === 'pin'}
                titulo={`¿Pedir a ${nombre} un PIN nuevo?`}
                texto="Borramos su PIN y le avisamos para que elija otro en Mi cuenta. Hasta entonces no podrá entrar en las tablets."
                accion="Pedir un PIN nuevo"
                accionIcono="key-round"
                ocupado={ocupado}
                ocupadoTexto="Borrando…"
                movil={c.movil}
                onAccion={pedirPin}
                onCerrar={() => {
                    setVentana(null);
                }}
            />
        </>
    );
}

